import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pillarArticles } from '@/mocks/articles';

const articleCardImages: Record<string, string> = {
  'allchinabuy-review':
    'https://readdy.ai/api/search-image?query=Elegant%20laptop%20on%20a%20clean%20marble%20desk%20showing%20star%20rating%20interface%20warm%20desk%20lamp%20golden%20light%20sophisticated%20workspace%20editorial%20product%20photography%20neutral%20warm%20tones&width=600&height=400&seq=achb-card-review-v3&orientation=landscape',
  'allchinabuy-shipping-cost':
    'https://readdy.ai/api/search-image?query=Small%20premium%20parcel%20shipping%20box%20with%20label%20sitting%20on%20rustic%20wooden%20table%20warm%20window%20light%20editorial%20photography%20earthy%20tones%20organized%20composition&width=600&height=400&seq=achb-card-ship-v3&orientation=landscape',
  'allchinabuy-coupon':
    'https://readdy.ai/api/search-image?query=Bold%20discount%20tag%20clipped%20to%20a%20brown%20paper%20luxury%20shopping%20bag%20clean%20white%20background%20bright%20editorial%20product%20photography%20warm%20tones%20graphic%20minimal&width=600&height=400&seq=achb-card-coupon-v3&orientation=landscape',
  'allchinabuy-discount-code':
    'https://readdy.ai/api/search-image?query=Sleek%20smartphone%20on%20white%20desk%20showing%20promotional%20code%20entry%20screen%20modern%20minimalist%20product%20photography%20warm%20neutral%20tones%20soft%20professional%20lighting&width=600&height=400&seq=achb-card-discount-v3&orientation=landscape',
  'allchinabuy-warehouse':
    'https://readdy.ai/api/search-image?query=Modern%20clean%20warehouse%20shelf%20with%20neatly%20stacked%20cardboard%20boxes%20warm%20LED%20strip%20lighting%20professional%20interior%20photography%20organized%20aesthetic%20neutral%20tones%20no%20people&width=600&height=400&seq=achb-card-ware-v3&orientation=landscape',
  'allchinabuy-legit':
    'https://readdy.ai/api/search-image?query=Gold%20security%20shield%20badge%20resting%20on%20dark%20slate%20surface%20dramatic%20side%20lighting%20warm%20golden%20tones%20elegant%20premium%20product%20photography%20minimal%20composition&width=600&height=400&seq=achb-card-legit-v3&orientation=landscape',
  'allchinabuy-qc':
    'https://readdy.ai/api/search-image?query=Macro%20close-up%20of%20premium%20fabric%20texture%20showing%20intricate%20stitching%20seam%20quality%20clean%20white%20background%20detailed%20product%20photography%20warm%20neutral%20tones%20professional%20lighting&width=600&height=400&seq=achb-card-qc-v3&orientation=landscape',
  'allchinabuy-shipping':
    'https://readdy.ai/api/search-image?query=Row%20of%20colorful%20shipping%20labels%20on%20clipboard%20with%20faint%20world%20map%20background%20warm%20office%20lighting%20editorial%20documentary%20photography%20neutral%20tones%20organized%20workspace&width=600&height=400&seq=achb-card-shipping-v3&orientation=landscape',
  'allchinabuy-alternatives':
    'https://readdy.ai/api/search-image?query=Two%20similar%20fashion%20products%20side%20by%20side%20on%20clean%20comparison%20table%20with%20printed%20checklist%20pen%20clean%20white%20background%20overhead%20editorial%20photography%20warm%20neutral%20tones&width=600&height=400&seq=achb-card-alt-v3&orientation=landscape',
  'allchinabuy-tracking':
    'https://readdy.ai/api/search-image?query=Smartphone%20displaying%20package%20tracking%20map%20pin%20icon%20held%20in%20hand%20against%20blurred%20window%20natural%20warm%20light%20cozy%20lifestyle%20photography%20neutral%20tones%20soft%20focus&width=600&height=400&seq=achb-card-track-v3&orientation=landscape',
};

const categoryBadges: Record<string, string> = {
  'allchinabuy-review': 'Review',
  'allchinabuy-shipping-cost': 'Shipping',
  'allchinabuy-coupon': 'Coupon',
  'allchinabuy-discount-code': 'Discount',
  'allchinabuy-warehouse': 'Warehouse',
  'allchinabuy-legit': 'Safety',
  'allchinabuy-qc': 'QC Guide',
  'allchinabuy-shipping': 'Shipping',
  'allchinabuy-alternatives': 'Comparison',
  'allchinabuy-tracking': 'Tracking',
};

const readTimes: Record<string, string> = {
  'allchinabuy-review': '8 min read',
  'allchinabuy-shipping-cost': '9 min read',
  'allchinabuy-coupon': '7 min read',
  'allchinabuy-discount-code': '6 min read',
  'allchinabuy-warehouse': '10 min read',
  'allchinabuy-legit': '11 min read',
  'allchinabuy-qc': '9 min read',
  'allchinabuy-shipping': '10 min read',
  'allchinabuy-alternatives': '12 min read',
  'allchinabuy-tracking': '7 min read',
};

export default function ArticlesSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] ?? 'en';

  return (
    <section id="articles" className="py-20 md:py-28 bg-background-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-3">
            {t('home_articles.badge')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900 mb-4">
            {t('home_articles.title')}
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            {t('home_articles.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pillarArticles.map((article) => {
            const data = (article as Record<string, unknown>)[lang] as
              | { title: string; intro: string }
              | undefined;
            if (!data) return null;
            const cardImg = articleCardImages[article.slug] ?? article.image;
            return (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-white border border-background-200/70 rounded-2xl overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer flex flex-col"
              >
                <div className="w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={cardImg}
                    alt={data.title}
                    title={data.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                      {categoryBadges[article.slug] ?? 'Guide'}
                    </span>
                    <span className="text-foreground-400 text-xs">
                      {readTimes[article.slug] ?? '8 min read'}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
                    {data.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {data.intro}
                  </p>
                  <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors duration-200 whitespace-nowrap mt-auto">
                    {t('home.articles.readMore')}
                    <i className="ri-arrow-right-line ml-1.5 group-hover:translate-x-1 transition-transform duration-200"></i>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <p className="text-foreground-500 text-sm">{t('home_articles.more')}</p>
        </div>
      </div>
    </section>
  );
}